import { Box, Button, Chip, Divider, Fab, Typography } from '@mui/material';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { useAtomValue } from 'jotai';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router'
import { Business_Profile_Enum, User_Role_Enum } from '@kheops/graphql';
import { GroupsOutlined } from '@mui/icons-material';
import { RoutePaths } from '../../routes/AppRoutes';
import { currentContextAtom } from '../../state';
import { GetCompanyUsersQuery, useGetCompanyUsersQuery } from '../../queries/__generated__/getCompanyUsers.generated';
import SearchInput from '../../common/components/SearchInput';
import { insensitiveStringIncludes } from '../../common/utils/common.utils';
import TopAppBar from '../../common/components/TopAppBar';
import CompanyTeamUserRow from './CompanyTeamUserRow';
import { useCompanyTeamRoleWording } from './useCompanyTeamRoleWording';

export interface UserRowData {
  role: User_Role_Enum;
  user: GetCompanyUsersQuery['role_assignment'][0]['user'];
  productFamilies: GetCompanyUsersQuery['role_assignment'][0]['product_families'];
  isAdmin?: boolean;
}

export const BUYER_ROLES = [
  User_Role_Enum.BuyerDirector,
  User_Role_Enum.BuyerFinance,
  User_Role_Enum.BuyerManager,
  User_Role_Enum.BuyerOwner,
];

export const SUPPLIER_ROLES = [
  User_Role_Enum.SupplierOwner,
  User_Role_Enum.SupplierSalesAdmin,
  User_Role_Enum.SupplierSalesRep,
];

export default function CompanyTeamPage(): React.JSX.Element {
  const { t } = useTranslation(['common', 'company']);
  const navigate = useNavigate();
  const { companyId, realm, companyBrand } = useAtomValue(currentContextAtom);
  const { data } = useGetCompanyUsersQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });
  const [query, setQuery] = useState('');
  const [filterRole, setFilterRole] = useState<User_Role_Enum | 'ALL'>('ALL');
  const resolvedWording = useCompanyTeamRoleWording(companyBrand || undefined);
  const availableRoles = realm === Business_Profile_Enum.Buyer ? BUYER_ROLES : SUPPLIER_ROLES;

  const users = useMemo(() => {
    return data?.role_assignment.reduce(
      (acc, value) => {
        const existingUser = acc.find(({ user: { id } }) => value.user.id === id);
        const isAdmin = value.role_name === User_Role_Enum.CompanyAdmin;

        if (existingUser) {
          if (isAdmin) {
            existingUser.isAdmin = isAdmin;
          } else {
            existingUser.role = value.role_name!;
          }
        } else {
          acc.push({
            role: value.role_name!,
            user: value.user,
            productFamilies: value.product_families,
            isAdmin,
          });
        }

        return acc;
      },
      [] as UserRowData[],
    ) || [];
  }, [data?.role_assignment]);

  const usersFiltered = useMemo(() => {
    return users.filter(({ user, role }) => {
      const matchQuery = insensitiveStringIncludes(user.first_name, query) || insensitiveStringIncludes(user.last_name, query) || insensitiveStringIncludes(user.email, query);
      const matchRole = filterRole === 'ALL' || filterRole === role;

      return matchQuery && matchRole;
    });
  }, [users, query, filterRole]);

  return (
    <>
      <TopAppBar
        backTo={RoutePaths.NAVIGATION_MAIN}
        configuration="small"
        title={t('common:team')}
        shouldCheckHistory={false}
        sx={{
          position: 'fixed',
        }}
      />
      <Box
        sx={{
          p: {
            xs: 0,
            lg: 2,
          },
          mt: {
            xs: 6.5,
            lg: 2,
          },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: {
              xs: 'none',
              lg: 'flex',
            },
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <GroupsOutlined height={32} width={32} />
          <Typography variant="titleLarge">
            {t('common:team')}
          </Typography>
          <Box sx={{ flex: 1 }}/>
          <Button
            variant="outlined"
            component={Link}
            to={RoutePaths.COMPANY_TEAM_CREATE}
            startIcon={<PersonAddAltIcon />}
          >
            {t('company:create_account')}
          </Button>
        </Box>
        <SearchInput
          onChange={setQuery}
          sx={{
            width: {
              xs: '100%',
              lg: 420,
            },
          }}
        />
        <Box sx={{ display: 'flex', gap: 1, maxWidth: '100%', overflow: 'auto' }}>
          <Chip
            variant="filter"
            label={`${t('company:all_roles')} (${users.length})`}
            className={filterRole === 'ALL' ? 'Kheops-selected' : ''}
            onClick={() => setFilterRole('ALL')}
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              my: 0.5,
            }}
          />
          {availableRoles.map((role) => (
            <Chip
              key={role}
              variant="filter"
              label={`${t(resolvedWording[role])} (${
                users.reduce((acc, value) => acc + (value.role === role ? 1 : 0), 0)
              })`}
              className={filterRole === role ? 'Kheops-selected' : ''}
              onClick={() => setFilterRole(role)}
            />
          ))}

        </Box>
        <Box sx={{ width: '100%' }}>
          {usersFiltered.map((userData) => (
            <CompanyTeamUserRow data={userData} key={userData.user.id} />
          ))}
        </Box>
      </Box>
      <Fab
        color="primary"
        onClick={() => navigate(RoutePaths.COMPANY_TEAM_CREATE)}
        sx={{
          display: {
            xs: 'flex',
            sm: 'none',
          },
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
      >
        <PersonAddAltIcon />
      </Fab>
    </>
  );
}
